import React, { ReactNode } from 'react'

// Components
import {
  Dropdown,
  SelectDropdownLinkItem,
  SelectDropdownActionDropdown,
} from '@sketch/components'

import { routes, DroppableLinkItem } from '@sketch/modules-common'

// Menu items components
import { DesignSystemsNotEnabled } from 'modules/designSystems/components/DesignSystemsSidebar/DesignSystemsNotEnabled'
import { PublicProfileSidebarItem } from 'modules/community/components/PublicProfileSidebarItem'

// Local Icons
import { icons } from './icons'
import HelpMenu from './components/HelpMenu/OldHelpMenu'

// Styles
import { StyledPill } from './Sidebar.styles'

// Types
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

export interface MenuItem {
  item: ReactNode
  hidden?: boolean
  key: string
  isActive?: boolean
}

export const menuItemsText = {
  account: 'Account',
  updates: 'Updates',
  settings: 'People & Settings',
  people: 'People',
  allDocuments: 'All Documents',
  sharedWithMe: 'Shared with Me',
  libraries: 'Libraries',
  designSystems: 'Design Systems',
  templates: 'Templates',
  trash: 'Trash',
  drafts: 'My Drafts',
  help: 'Help with Sketch',
  discover: 'Discover',
}

type MenuItemsProps = {
  workspaceId: string
  workspaceName: string
  isDiscoverFFEnabled?: boolean
  showWorkspaceSettings?: boolean
  showUnseenNotificationBadge: boolean
  showUpdatesLink: boolean
  showDiscoverLink: boolean
  showHelpLink: boolean
  userRole: WorkspaceMinimalFragment['userRole']
  userCanAdminister: boolean
}

type WorkspaceMenuItemsProps = {
  workspaceId: string
  canUseDesignSystemManager: boolean
  userRole: WorkspaceMinimalFragment['userRole']
  hasDraftsProject: boolean
  hasWorkspaceTemplatesEnabled: boolean
  canUserAdministerShares: boolean
  handleOnEmptyTrash: () => void
  deleteShare: ({ identifier }: { identifier: string }) => void
  isProfileEnabled: boolean
}

// Menu items
export const getMenuItems = ({
  workspaceId,
  workspaceName,
  showWorkspaceSettings,
  showUnseenNotificationBadge,
  showUpdatesLink,
  showDiscoverLink,
  showHelpLink,
  userRole,
  userCanAdminister,
}: MenuItemsProps): MenuItem[] => {
  const isFinance = userRole === 'FINANCE'
  const isAdmin = userRole === 'ADMIN'
  const isPartner = userRole === 'PARTNER'

  return [
    {
      // TODO: Remove this item when the new sidebar is released
      // https://github.com/sketch-hq/Cloud/issues/15634
      item: (
        <SelectDropdownLinkItem
          icon={icons.BellIcon}
          text={menuItemsText.updates}
          additional={
            showUnseenNotificationBadge ? (
              <StyledPill variant="secondary">New</StyledPill>
            ) : undefined
          }
          to={routes.UPDATES.create({})}
        />
      ),
      hidden: isFinance || isPartner || !showUpdatesLink,
      key: 'updates',
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={
            isAdmin || isFinance || isPartner ? icons.GearIcon : icons.EmailIcon
          }
          text={
            isAdmin || isFinance || isPartner
              ? menuItemsText.settings
              : menuItemsText.people
          }
          to={routes.WORKSPACE_SETTINGS.create({ workspaceId })}
        />
      ),
      hidden: !showWorkspaceSettings,
      key: 'workspace-settings',
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.CompassIcon}
          text={menuItemsText.discover}
          to={routes.WORKSPACE_DISCOVER.create({ workspaceId })}
        />
      ),
      hidden: isFinance || isPartner || !showDiscoverLink,
      key: 'discover',
    },
    {
      item: (
        <HelpMenu
          userCanAdminister={userCanAdminister}
          workspaceId={workspaceId}
          workspaceName={workspaceName}
        />
      ),
      key: 'help-menu',
      hidden: !showHelpLink,
    },
  ]
}

export const getWorkspaceMenuItems = ({
  workspaceId,
  canUseDesignSystemManager,
  userRole,
  hasDraftsProject,
  hasWorkspaceTemplatesEnabled,
  canUserAdministerShares,
  handleOnEmptyTrash,
  deleteShare,
  isProfileEnabled,
}: WorkspaceMenuItemsProps): MenuItem[] => {
  const isFinance = userRole === 'FINANCE'
  const isAdmin = userRole === 'ADMIN'

  const getIsActive = (route: string) => window.location.pathname === route

  return [
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.AllDocuments}
          text={menuItemsText.allDocuments}
          to={routes.WORKSPACE_SHARES.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'all-documents',
      isActive: getIsActive(routes.WORKSPACE_SHARES.create({ workspaceId })),
    },
    {
      item: (
        <SelectDropdownLinkItem
          /*
        Designers could not come up with one icon that looks perfect on both
        1x and 2x aspect ratio screens at the same time, so they created two.
        */
          icon={icons.MyDraftsProject}
          text={menuItemsText.drafts}
          to={routes.WORKSPACE_DRAFTS.create({ workspaceId })}
        />
      ),
      hidden: isFinance || !hasDraftsProject,
      key: 'drafts',
      isActive: getIsActive(routes.WORKSPACE_DRAFTS.create({ workspaceId })),
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.SharedWithMe}
          text={menuItemsText.sharedWithMe}
          to={routes.WORKSPACE_SHARED_WITH_ME.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'shared-with-me',
      isActive: getIsActive(
        routes.WORKSPACE_SHARED_WITH_ME.create({ workspaceId })
      ),
    },
    {
      item: (
        <PublicProfileSidebarItem workspaceId={workspaceId} isAdmin={isAdmin} />
      ),
      hidden: !isProfileEnabled || isFinance,
      key: 'publicProfile',
      isActive: getIsActive(
        routes.WORKSPACE_PROFILE_EDIT.create({ workspaceId })
      ),
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.LinkLibrary}
          text={menuItemsText.libraries}
          to={routes.WORKSPACE_LIBRARIES.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'libraries',
      isActive: getIsActive(routes.WORKSPACE_LIBRARIES.create({ workspaceId })),
    },
    {
      item: <DesignSystemsNotEnabled />,
      hidden: canUseDesignSystemManager,
      key: 'designSystems',
    },
    {
      item: (
        <SelectDropdownLinkItem
          icon={icons.TemplateIcon}
          text={menuItemsText.templates}
          to={routes.WORKSPACE_TEMPLATES.create({ workspaceId })}
        />
      ),
      hidden: isFinance || !hasWorkspaceTemplatesEnabled,
      key: 'templates',
      isActive: getIsActive(routes.WORKSPACE_TEMPLATES.create({ workspaceId })),
    },

    {
      item: (
        <DroppableLinkItem
          icon={icons.Trash}
          text={menuItemsText.trash}
          to={routes.WORKSPACE_TRASH.create({ workspaceId })}
          action={
            isAdmin ? (
              <SelectDropdownActionDropdown
                text="Trash Options"
                content={
                  <>
                    <Dropdown.Header>Trash</Dropdown.Header>
                    <Dropdown.Item
                      intent="negative"
                      onClick={e => {
                        e.preventDefault()
                        handleOnEmptyTrash()
                      }}
                    >
                      Empty&hellip;
                    </Dropdown.Item>
                  </>
                }
              />
            ) : null
          }
          onDropped={({ shareId }) => deleteShare({ identifier: shareId })}
        />
      ),
      hidden: isFinance || !canUserAdministerShares,
      key: 'trash',
      isActive: getIsActive(routes.WORKSPACE_TRASH.create({ workspaceId })),
    },
  ]
}
